import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { SectionContainer } from "@/components/shared/SectionContainer";
import { ProjectCard } from "@/components/project/ProjectCard";
import { api } from "@/lib/api";
import type { Project } from "@portfolio/types";
import { Link } from "react-router-dom";

export function FeaturedProjects() {
  const { data } = useQuery({
    queryKey: ["projects", "featured"],
    queryFn: () => api.get<Project[]>("/api/projects/featured"),
  });

  const featured: Project[] = data ?? [];

  return (
    <SectionContainer className="border-t border-border/50">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary">Selected work</div>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Featured Projects</h2>
          <p className="mt-3 max-w-xl text-muted-foreground">
            Production apps, ML pipelines, and research repos — all live, all maintained.
          </p>
        </div>
        <Link
          to="/projects"
          className="hidden items-center gap-1 text-sm text-primary hover:underline sm:inline-flex"
        >
          See all <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {featured.length === 0 ? (
          <p className="col-span-full text-muted-foreground">
            Projects will appear here once the admin dashboard publishes them.
          </p>
        ) : (
          featured.map((project, i) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <ProjectCard project={project} />
            </motion.div>
          ))
        )}
      </div>
    </SectionContainer>
  );
}
